import { isDemoMode } from "utils/isDemoMode";

/**
 * Carriers whose lockers and shops ("out of home" points) a shipment can be
 * sent to. The server says which ones the login can use; a carrier it does not
 * list keeps its tab, saying it is not available yet.
 */
export enum PickupPointCarrier {
  EVRI = "EVRI",
  INPOST = "INPOST",
}

export const PICKUP_POINT_CARRIER_NAMES: Record<PickupPointCarrier, string> = {
  [PickupPointCarrier.EVRI]: "Evri",
  [PickupPointCarrier.INPOST]: "InPost",
};

// The tabs of the "Choose a locker or shop" window, in this order.
export const PICKUP_POINT_TABS: {
  carrier: PickupPointCarrier;
  title: string;
}[] = [
  { carrier: PickupPointCarrier.EVRI, title: "Evri parcel lockers" },
  { carrier: PickupPointCarrier.INPOST, title: "InPost parcel lockers" },
];

export enum PickupPointType {
  LOCKER = "LOCKER",
  SHOP = "SHOP",
}

export type PickupPointOpeningHours = {
  [day: string]: { start: string; end: string }[];
};

/** A locker or shop as the server returns it, whichever carrier runs it. */
export type IPickupPoint = {
  carrier: PickupPointCarrier;
  id: string;
  type: PickupPointType;
  name: string;
  addressLine1: string;
  city: string;
  zip: string;
  countryIsoCode: string;
  distanceMetres: number | null;
  open247: boolean;
  description: string;
  openingHours: PickupPointOpeningHours | null;
  // Where the point is, for the map. Null when the carrier does not say.
  latitude: number | null;
  longitude: number | null;
  // A photo of the locker or shop front, when the carrier has one.
  imageUrl: string;
};

/** Where to search: near a postcode, or around a point on the map. */
export type PickupPointSearch =
  | { postcode: string }
  | { latitude: number; longitude: number };

/** What a shipment stores in serviceOptions.pickupPoint. */
export type IStoredPickupPoint = Pick<
  IPickupPoint,
  "carrier" | "id" | "type" | "name"
>;

/**
 * A stored locker or shop (on a shipment or an address book entry) as a full
 * point. The address stored with it holds the point's name in line 1 and its
 * street in line 2.
 */
export const pickupPointFromStored = (
  stored: IStoredPickupPoint,
  address: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    zip?: string | null;
    countryIsoCode?: string | null;
  }
): IPickupPoint => ({
  carrier: stored.carrier,
  id: stored.id,
  type: stored.type,
  name: stored.name || address.line1 || "",
  addressLine1: address.line2 || "",
  city: address.city || "",
  zip: address.zip || "",
  countryIsoCode: address.countryIsoCode || "GB",
  distanceMetres: null,
  open247: false,
  description: "",
  openingHours: null,
  latitude: null,
  longitude: null,
  imageUrl: "",
});

// What each carrier's lockers and shops accept, shown under the locker search.
export const PICKUP_POINT_PARCEL_LIMITS: Partial<
  Record<PickupPointCarrier, string>
> = {
  [PickupPointCarrier.INPOST]:
    "InPost lockers and shops take parcels up to 64 x 41 x 38 cm and 15 kg.",
};

// Locker and shop delivery is in a pilot: only these logins are offered it
// (Joe's live and staging logins, and Nathan Rankine's ITD and test logins).
// The server applies the same list and refuses locker quotes and bookings
// from anyone else. On the demo site everyone has it.
export const PICKUP_POINT_PILOT_EMAILS = [
  "jlei@itdglobal.com",
  "jlei523@gmail.com",
  "nathan.rankine@itdglobal.com",
  "nathanrankine@outlook.com",
];

export const isPickupPointPilotUser = (email: string | null | undefined) =>
  isDemoMode() ||
  PICKUP_POINT_PILOT_EMAILS.includes((email || "").trim().toLowerCase());
